import { PillarChip } from '../../components/PillarChip'
import { formatMoney } from '../../lib/format'
import { parseNarrative, weekLabel } from '../../lib/weekly'
import type { WeeklyView } from '../../types/weekly'
import type { RailPick } from '.'

// The week at a glance: the pipeline named now against the last update, movement per slice as a diverging bar
// (down on the left, up on the right), the headline of the written narrative, and the eight numbers that matter.
// Nothing here is interactive beyond jumping to a slice; the cards below carry the evidence.
export function Glance({ view, onPick }: { view: WeeklyView; onPick: (p: RailPick) => void }) {
  const m = view.movement
  const g = m.glance
  const delta = g.pipelineBefore != null ? g.pipelineNow - g.pipelineBefore : null
  const headline = view.narrative ? parseNarrative(view.narrative.text)[0]?.paragraphs[0]?.filter((s) => s.type === 'text').map((s) => (s.type === 'text' ? s.text : '')).join('').trim() : null
  const accounts = m.items.filter((i) => i.slice === 'accounts').slice(0, 4)

  return (
    <section className="glance weekly-glance">
      <div className="glance-pipeline">
        <p className="kicker">Pipeline named</p>
        <p className="glance-big">{g.pipelineNow > 0 ? formatMoney(g.pipelineNow) : 'none'}</p>
        <p className="glance-axes">
          {delta != null
            ? <span><strong className={delta >= 0 ? 'up' : 'down'}>{delta >= 0 ? '+' : ''}{formatMoney(delta)}</strong> since last update</span>
            : <span><strong>{g.openPursuits}</strong> open pursuit{g.openPursuits === 1 ? '' : 's'}</span>}
          {delta != null && <span><strong>{g.openPursuits}</strong> open</span>}
        </p>
        <MovementBars view={view} onPick={onPick} />
      </div>

      <div className="glance-words">
        <p className="kicker">The week, in a line</p>
        {headline
          ? <p className="glance-summary">{headline}</p>
          : <p className="glance-summary muted">Not written yet. The movement below is measured; the words come when the week is written{view.modelConfigured ? '.' : ', laid out from the facts since no model is connected.'}</p>}
        <dl className="glance-conventions">
          <dt>Week</dt>
          <dd><span className="chip">{weekLabel(m.week)}</span>{m.measuredFrom.fromStoredWeek && <span className="chip">as written</span>}</dd>
          <dt>Read</dt>
          <dd>
            <span className="chip"><em>{m.measuredFrom.threadsActive}</em> threads</span>
            <span className="chip"><em>{m.measuredFrom.meetingsHeld}</em> meetings</span>
            <span className="chip"><em>{m.measuredFrom.signalsRaised}</em> signals</span>
            <span className="chip"><em>{m.measuredFrom.contactsTouched}</em> contacts touched</span>
          </dd>
          <dt>Against</dt>
          <dd>{m.measuredFrom.hasBaseline ? <span className="chip">the update of {m.measuredFrom.baselineTakenOn}</span> : <span className="chip never">no earlier update</span>}<PillarChip pillar={m.measuredFrom.pillar} /></dd>
        </dl>
      </div>

      <div className="glance-stats weekly-stats">
        <Stat n={g.newPursuits} label="new pursuits" onClick={() => onPick('pipeline')} />
        <Stat n={g.stageUps} label="stage up" tone="up" onClick={() => onPick('pipeline')} />
        <Stat n={g.stageDowns} label="stage down" tone={g.stageDowns > 0 ? 'down' : undefined} onClick={() => onPick('pipeline')} />
        <Stat text={g.amountsNamed > 0 ? formatMoney(g.amountNamed) : '—'} label={`named this week · ${g.amountsNamed}`} onClick={() => onPick('pipeline')} />
        <Stat n={g.meetingsHeld} label="external meetings" onClick={() => onPick('meetings')} />
        <Stat n={g.dormantResurfaced} label="dormant resurfaced" onClick={() => onPick('people')} />
        <Stat text={`${g.tacticsApplied} · ${g.tacticResults}`} label="tactics applied · results" onClick={() => onPick('tactics')} />
        <Stat n={g.nextStepsOwed} label="next steps you owe" tone={g.nextStepsOwed > 0 ? 'warn' : undefined} />
        <Stat n={g.threats ?? 0} label="competitor threats" tone={(g.threats ?? 0) > 0 ? 'down' : undefined} />
        <div className="glance-busiest">
          <p className="kicker">Where it moved</p>
          {accounts.length === 0 && <p className="muted">No account moved this week.</p>}
          <ul>
            {accounts.map((a) => (
              <li key={a.id}>
                <button className="link-button" onClick={() => onPick('accounts')}>{a.title}</button>
                <span className="muted">{a.callout}{a.amount ? ` · ${a.amount > 0 ? '+' : ''}${formatMoney(a.amount)}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function Stat({ n, text, label, tone, onClick }: { n?: number; text?: string; label: string; tone?: 'up' | 'down' | 'warn'; onClick?: () => void }) {
  const body = (
    <>
      <strong className={tone ?? ''}>{text ?? (n ?? 0).toLocaleString()}</strong>
      <span>{label}</span>
    </>
  )
  return onClick ? <button className="glance-stat as-button" onClick={onClick}>{body}</button> : <div className="glance-stat">{body}</div>
}

// Movement per slice as a diverging bar: what moved down on the left of the axis, up on the right, the rest as a
// quiet band. Widths are counts on a shared scale so the slices read against one another.
function MovementBars({ view, onPick }: { view: WeeklyView; onPick: (p: RailPick) => void }) {
  const slices = view.movement.slices
  // square-root scale: a first scan can land a thousand contacts in one week; eight pursuits must still read beside them
  const max = Math.max(1, ...slices.map((s) => Math.max(s.ups, s.downs, s.count - s.ups - s.downs)))
  const w = 260, rowH = 18, axis = w / 2, half = axis - 4
  const scale = (n: number) => (Math.sqrt(n) / Math.sqrt(max)) * half
  return (
    <svg className="movement-bars" viewBox={`0 0 ${w} ${slices.length * rowH + 6}`} width={w} role="img" aria-label="Movement per slice: down on the left, up on the right">
      <line x1={axis} y1={0} x2={axis} y2={slices.length * rowH + 4} className="axis" />
      {slices.map((s, i) => {
        const y = i * rowH + 4
        const flat = s.count - s.ups - s.downs
        return (
          <g key={s.slice} className="row" onClick={() => onPick(s.slice)} style={{ cursor: 'pointer' }}>
            <title>{`${s.title}: ${s.ups} up, ${s.downs} down, ${flat} other`}</title>
            {s.downs > 0 && <rect x={axis - scale(s.downs)} y={y} width={scale(s.downs)} height={rowH - 8} className="down" rx={2} />}
            {s.ups > 0 && <rect x={axis} y={y} width={scale(s.ups)} height={rowH - 8} className="up" rx={2} />}
            {flat > 0 && <rect x={axis + scale(s.ups)} y={y + 3} width={scale(flat)} height={rowH - 14} className="flat" rx={1} />}
            <text x={axis - scale(s.downs) - 6} y={y + rowH - 9} textAnchor="end" className="label">{s.title}</text>
            <text x={axis + scale(s.ups + Math.max(0, flat)) + 6} y={y + rowH - 9} className="count">{s.count}</text>
          </g>
        )
      })}
    </svg>
  )
}
